import { Trans } from '@lingui/macro';
import {
  ProductPrice,
  ProductCardBadge,
  SocialShare,
  AddToCart,
  LoginForProPriceLabel,
} from '@we-make-websites/ui-lib';
import type { StoreMoney } from '@we-make-websites/ui-lib';
import { LoadingText } from '@we-make-websites/ui-lib/src/components/ui/loading/LoadingText';
import clsx from 'clsx';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import type { ReactNode } from 'react';
import { useRef, useState, useEffect } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm, Controller } from 'react-hook-form';
import { useIntersectionObserverRef } from 'rooks';
import aa from 'search-insights';
import { useContext } from 'use-context-selector';
import { localesWithBazaarVoice, storeLocale } from '@/root/constants';
import { BvRating } from 'components/bv/bv-rating';
import { CartContext } from 'components/cartContext/CartContext';
import { CustomerContext } from 'components/customerContext/CustomerContext';
import { useFeatureFlags } from 'components/featureFlagsProvider/FeatureFlagsContext';
import { OverlayContext } from 'components/overlay/OverlayContext';
import { ProductContext } from 'components/productContext/ProductProvider';
import type { StoreMediaImage } from 'components/productContext/types/storeMedia';
import { StoreMediaType } from 'components/productContext/types/storeMedia';
import { ProductQuantitySelectorWithState } from 'components/productQuantitySelector/ProductQuantitySelector';
import { ProductTitle } from 'components/productTitle/ProductTitle';
import { ProductType } from 'components/productType/ProductType';
import { ProductVariantSelector } from 'components/productVariantSelector/ProductVariantSelector';
import { StickyProductForm } from 'components/stickyProductForm/StickyProductForm';
import { algoliaProductIndex } from 'utils/algoliaConstants';
// import IconNail from 'icons/misc/nail.svg';
import { sendDataToGTM } from 'utils/gtm/gtm';
import { WishlistButtonConnected } from '../wishlistButton/WishlistButtonConnected';
import styles from './ProductForm.module.scss';

const BackInStockModal = dynamic(
  () => import('components/backInStockModal/BackInStockModal'),
  {
    ssr: false,
  }
);

type ProductFormInput = {
  quantity: number;
};

export const ProductForm = ({
  colorSelector,
}: {
  colorSelector?: ReactNode;
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const { signedIn } = useContext(CustomerContext);
  const { locale: routerLocale, query } = useRouter();
  const locale = storeLocale(routerLocale);

  const productContext = useContext(ProductContext);
  if (productContext === undefined)
    throw new Error('ProductForm must be used within a ProductContext');

  const cartContext = useContext(CartContext);
  if (cartContext === undefined)
    throw new Error('ProductForm must be used within a CartContext');

  const { featureFlags } = useFeatureFlags();

  const formRef = useRef<HTMLFormElement>(null);
  const [stickyATCVisible, setStickyATCVisible] = useState(false);
  const { open } = useContext(OverlayContext);

  const {
    id,
    title,
    description,
    price,
    comparePrice,
    selectedVariant,
    isLoading,
    proCustomer,
    proOnly,
    variants,
    media,
    selectVariant,
    tags,
    url,
    productType,
    ultaProductUrl,
    customBuyNowUrl,
    redirectToWsUrl,
    asin,
    separatePriceForPro,
    colorDepth,
    primaryColorFamily,
    secondaryColorFamily,
    colorFamilySubgroup,
    finish,
    colorSystemLabel,
    hexColour,
    cadColorOpacity,
    sku,
    seo,
    collections,
    recommendedEssentials,
    handle,
  } = productContext;

  const toggleUpSells = !!recommendedEssentials.length;

  useEffect(() => {
    const gtmProduct = [
      {
        name: title,
        id: sku,
        price: price?.amount,
        brand: 'OPI Products',
        category: colorSystemLabel,
        variant: selectedVariant?.title,
        dimension2: primaryColorFamily,
        dimension6: secondaryColorFamily,
        dimension7: colorFamilySubgroup,
        dimension8: finish,
        dimension9: colorDepth,
        dimension10: cadColorOpacity,
        dimension12: hexColour,
        dimension13: title,
        dimension14: colorSystemLabel,
        dimension3: selectedVariant?.availableForSale
          ? 'IN STOCK'
          : 'OUT OF STOCK',
      },
    ];

    const productDetailView = {
      event: 'productDetailView',
      ecommerce: {
        currencyCode: price?.currencyCode,
        detail: {
          actionField: {
            list: productType,
          },
        },
        products: gtmProduct,
      },
    };

    const colorAsDataEvent = {
      ColorFamilyPrimary: primaryColorFamily,
      ColorFamilySecondary: secondaryColorFamily,
      ColorFamilySubGroup: colorFamilySubgroup,
      Finish: finish,
      ColorDepth: colorDepth,
      ColorShadeName: title,
      ColorSystem: colorSystemLabel,
      'Hex Code': hexColour,
    };

    sendDataToGTM(colorAsDataEvent);
    sendDataToGTM(productDetailView);
  }, [
    sku,
    colorDepth,
    colorFamilySubgroup,
    colorSystemLabel,
    finish,
    hexColour,
    cadColorOpacity,
    id,
    price?.amount,
    price?.currencyCode,
    primaryColorFamily,
    productType,
    secondaryColorFamily,
    selectedVariant?.availableForSale,
    selectedVariant?.title,
    title,
  ]);

  /**
   * Is current selected variant available for sale
   */
  const availableForSale = selectedVariant?.availableForSale;

  const {
    amazonCart,
    backInStock,
    showPrice: showProductPrice,
    cartEnabled,
    mikmakEnabled,
  } = featureFlags[locale];

  /**
   * If product or selected variant is pro only and logged in customer is not a pro
   */
  const proOnlyHide = (selectedVariant?.proOnly || proOnly) && !proCustomer;
  /**
   * Used to identify enabled AMAZON cart and pro customers.
   */
  const proAmazonCart = amazonCart && proCustomer;
  /**
   * Used to check if AMAZON cart is enabled and consumer customer
   * and product do not have ASIN data populated in metafield.
   */
  const consumerAmazonCartNoProductASIN = amazonCart && !proCustomer && !asin;
  /**
   * A flag to decide if the form should be disabled or not.
   */

  const disableForm =
    proOnlyHide ||
    ultaProductUrl ||
    customBuyNowUrl ||
    proAmazonCart ||
    consumerAmazonCartNoProductASIN ||
    !availableForSale ||
    !cartEnabled;

  /**
   * Only show price if product has a price and pro only hide is false, and global feature flag is enabled.
   */
  const showPrice = price && !proOnlyHide && showProductPrice;

  // Get first image.
  const featuredMedia = media.find(
    (image) => image.type === StoreMediaType.Image
  ) as StoreMediaImage;

  const shouldLoadBvScript = localesWithBazaarVoice.includes(locale);

  const { cartLineAdd, cartLoading, cartErrors } = cartContext;

  const addToCart = (quantity: number) => {
    if (!selectedVariant) return;

    cartLineAdd({ id: selectedVariant.id, quantity }, toggleUpSells);
    if (query?.objectID) {
      if (!query?.queryID) {
        aa('convertedObjectIDs', {
          index: algoliaProductIndex(locale),
          eventName: 'Product Converted',
          objectIDs: [query.objectID as string],
          currency: price?.currencyCode,
          value: price?.amount,
        });
      } else {
        aa('addedToCartObjectIDsAfterSearch', {
          eventName: 'Product Converted After Search',
          index: algoliaProductIndex(locale),
          objectIDs: [query.objectID as string],
          queryID: query.queryID as string,
          objectData: [
            {
              quantity,
              price: price?.amount,
            },
          ],
          currency: price?.currencyCode,
          value: price?.amount,
        });
      }
    }
  };

  const { handleSubmit, control } = useForm<ProductFormInput>({
    defaultValues: {
      quantity: 1,
    },
  });

  const onSubmit: SubmitHandler<ProductFormInput> = (data) => {
    if (!availableForSale && backInStock) {
      open({
        ignoreDismissed: true,
        namespace: 'backInStock',
      });

      return;
    }

    if (disableForm) return;

    addToCart(data.quantity);
  };

  const addToCartIntersectionHandler = (
    entries: IntersectionObserverEntry[]
  ) => {
    if (entries && entries[0]) {
      const yPosition = entries[0].boundingClientRect.y;
      setStickyATCVisible(yPosition < 0);
    }
  };

  const [atcRef] = useIntersectionObserverRef(addToCartIntersectionHandler, {
    threshold: 1,
  });
  return (
    <>
      <NextSeo
        title={seo.title}
        description={seo.description}
        openGraph={{
          url,
          title: seo.title,
          description: seo.description,
        }}
      />
      <div
        className={clsx(
          styles.productForm,
          mikmakEnabled && 'product-quickview'
        )}
      >
        <div className={styles.productForm__header}>
          <ProductCardBadge
            product={{
              price: price as StoreMoney,
              comparePrice: comparePrice ?? undefined,
              proOnly,
              tags,
            }}
            className={styles.productForm__badge}
          />

          <ProductType
            className={clsx(
              'text-utility-utility-small-mobile text-utility-utility-small-desktop',
              styles.productForm__productType
            )}
            type={productType}
            collections={collections}
            isLoading={isLoading}
          />

          <WishlistButtonConnected
            handle={handle}
            className={styles.productForm__wishlist}
          />
        </div>

        <div className={styles.productForm__details}>
          <ProductTitle
            {...{ title, isLoading }}
            className={clsx(styles.productForm__productTitle, 'productTitle')}
          />

          <ProductDescription {...{ description, isLoading }} />

          {/* <ReviewStars {...reviews} className={styles.productForm__reviews} /> */}
          {shouldLoadBvScript && (
            <BvRating slug={handle} className={styles.productForm__reviews} />
          )}

          {showPrice && (
            <div className={styles.productForm__productPriceWrapper}>
              <ProductPrice
                {...{
                  price: selectedVariant?.price || price,
                  isLoading,
                }}
                type="unit"
                className={clsx(
                  styles.productForm__productPrice,
                  'text-heading-h7-desktop text-body-1-bold-mobile',
                  selectedVariant?.compareAtPrice &&
                    styles['productForm__productPrice--sale']
                )}
              />
              {selectedVariant?.compareAtPrice && (
                <ProductPrice
                  {...{
                    price: selectedVariant?.compareAtPrice,
                    isLoading,
                  }}
                  type="compare"
                  className={clsx(
                    styles.productForm__productPrice,
                    'text-heading-h7-desktop text-body-1-bold-mobile'
                  )}
                />
              )}

              {selectedVariant?.bundlePrice && (
                <ProductPrice
                  {...{
                    price: selectedVariant.bundlePrice,
                    isLoading,
                  }}
                  type="bundle"
                  className={clsx(
                    styles.productForm__productPrice,
                    styles['productForm__productPrice--bundle'],
                    'text-body-small-desktop text-body-small-mobile'
                  )}
                />
              )}
            </div>
          )}

          <LoginForProPriceLabel
            {...{
              locale,
              signedIn,
              separatePriceForPro,
              redirectToWsUrl,
            }}
          />
        </div>

        <form
          className={styles.productForm__form}
          ref={formRef}
          onSubmit={handleSubmit(onSubmit)}
        >
          <fieldset
            disabled={cartLoading || isLoading}
            className={styles.productForm__fieldset}
            data-form-errored={!!cartErrors}
            data-test-id="productFormFieldset"
          >
            <legend className="visually-hidden">
              <Trans id="product.form.legend">Product Form</Trans>
            </legend>

            <div
              className={clsx(
                styles.productForm__variantSelector,
                variants.length <= 1 && 'hidden'
              )}
            >
              <ProductVariantSelector
                namespace="main"
                {...{
                  variants,
                  selectedVariant,
                  selectVariant,
                  proCustomer,
                  isLoading,
                }}
              />
            </div>

            {colorSelector}

            <div
              className={clsx(
                styles.productForm__addToCartRow,
                (disableForm || (locale === 'en-GB' && proCustomer)) &&
                  styles['productForm__addToCartRow--full']
              )}
              ref={atcRef}
            >
              {!disableForm && !(locale === 'en-GB' && proCustomer) && (
                <Controller
                  control={control}
                  name="quantity"
                  render={({ field }) => (
                    <ProductQuantitySelectorWithState
                      {...field}
                      className={styles.productForm__quantity}
                      location="product"
                    />
                  )}
                />
              )}
              <AddToCart
                product={{
                  ...productContext,
                  proOnly: selectedVariant?.proOnly || proOnly,
                  availableForSale,
                  barcode: selectedVariant?.barcode,
                }}
                amazonCart={amazonCart}
                backInStock={backInStock}
                proCustomer={proCustomer}
                mikmakEnabled={mikmakEnabled}
                cartEnabled={cartEnabled}
              />
            </div>
            {locale === 'en-GB' && selectedVariant?.price && showPrice && (
              <klarna-placement
                data-key="credit-promotion-badge"
                data-locale={locale}
                data-purchase-amount={Math.round(
                  (selectedVariant?.price?.amount || price?.amount || 0) * 100
                )}
              />
            )}
          </fieldset>
        </form>

        {/* <Button
          type="button"
          modifiers="outlined"
          className={styles.productForm__tryOn}
          href="/"
        >
          <IconNail />
          <span className="text-utility-utility-mobile text-utility-utility-desktop">
            <Trans id="product.tryItOn" message="Try it on" />
          </span>
        </Button> */}

        <SocialShare
          text={title}
          image={featuredMedia ? featuredMedia.image.url : undefined}
          isLoading={isLoading}
        />

        <StickyProductForm visible={stickyATCVisible} />
        {!availableForSale && backInStock && <BackInStockModal />}
      </div>
    </>
  );
};

/**
 * Product short description.
 * Includes loading state.
 */
const ProductDescription = ({
  description,
  isLoading,
}: {
  description: string;
  isLoading: boolean;
}) => {
  return (
    <p
      className={clsx(
        styles.productForm__description,
        'text-body-small',
        isLoading && 'is-loading'
      )}
    >
      {isLoading && <LoadingText />}
      {!isLoading && description}
    </p>
  );
};
